/**
 * Casa Vincenzo - cart drawer first-add fix.
 *
 * The theme refreshes the drawer correctly whenever the cart already has
 * items, but not on the empty -> first item transition: the drawer opens
 * still showing "Your cart is empty" even though the cart has updated.
 * It fails silently, with no console error. That is the path every new
 * visitor takes, so it would be the first thing most buyers ever see.
 *
 * This listens for the same cart event the theme uses and, ONLY when the
 * drawer is currently in its empty state, re-fetches the drawer section and
 * swaps the inner node. Any other case returns immediately, so the normal
 * path keeps the theme's own handling and its view transition, and nothing
 * extra runs.
 *
 * LOCALE: a section fetched from "/" renders in the shop's default language.
 * Because this file is the only thing that fills the drawer on a first add,
 * getting the prefix wrong meant an Arabic shopper adding their first item saw
 * the whole drawer in English, inside an otherwise Arabic, right to left page.
 * See localeRoot below for why Shopify.routes.root alone was not enough.
 *
 * For reference, the theme's own refresh (assets/section-renderer.js) derives
 * its URL from window.location.href, so it is locale correct by construction.
 * This file is the only place that had to work the prefix out for itself.
 */

import { StandardEvents } from '@shopify/events';

(function () {
  if (window.__cvDrawerFill) return;
  window.__cvDrawerFill = true;

  const SECTION = 'cart-drawer-section';

  function pageLang() {
    const lang = document.documentElement.getAttribute('lang') || '';
    return lang.slice(0, 2).toLowerCase();
  }

  /**
   * The locale prefix to fetch the section from, e.g. "/ar/".
   *
   * Three sources, in order of how much they can be trusted, because no single
   * one holds in every surface the theme renders in:
   *
   * 1. Shopify.routes.root. The normal source, and correct on the storefront,
   *    where it reads "/ar/" on an Arabic page. But it is a global this file
   *    does not own: when it is missing, or has not been localised for the
   *    request, it reads "/" and we would silently fetch the default language.
   * 2. The first path segment, when it matches <html lang>. Covers a localised
   *    URL whose root global did not come through.
   * 3. Shopify.locale. The backstop for a surface that renders a non primary
   *    locale WITHOUT a locale path, which is what the theme editor preview
   *    can do. There the lang attribute and Shopify.locale both say "ar" while
   *    the path and the root say "/", and only this check saves it.
   *
   * Anything still unresolved falls through to root, which is the shop's
   * primary locale and the correct answer for an English page.
   */
  function localeRoot() {
    const lang = pageLang();
    const configured = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    const root = configured || '/';

    if (lang && (root === '/' + lang || root.indexOf('/' + lang + '/') === 0)) return root;

    const segment = location.pathname.split('/')[1] || '';
    if (lang && segment && segment.slice(0, 2).toLowerCase() === lang) {
      return '/' + segment + '/';
    }

    const locale = window.Shopify && window.Shopify.locale;
    if (lang && locale && locale.slice(0, 2).toLowerCase() === lang && root === '/') {
      return '/' + locale + '/';
    }

    return root;
  }

  function sectionUrl() {
    let base = localeRoot();
    // Trim a trailing slash without a regex: this file is written through a
    // GraphQL block string, where backslashes are literal, so an escaped
    // pattern would reach the browser malformed.
    if (base.charAt(base.length - 1) === '/') {
      base = base.slice(0, base.length - 1);
    }
    return base + '/?section_id=' + SECTION;
  }

  /**
   * Last line of defence. Arabic is the only non Latin locale on this shop, so
   * if the page is Arabic and the markup that came back contains no Arabic at
   * all, it was rendered in the wrong language. Leaving the drawer as the theme
   * left it is strictly better than swapping English content into an Arabic
   * page, so in that case we simply do not swap.
   */
  function languageMatches(markup) {
    if (pageLang() !== 'ar') return true;
    for (let i = 0; i < markup.length; i++) {
      const code = markup.charCodeAt(i);
      if (code >= 0x0600 && code <= 0x06ff) return true;
    }
    return false;
  }

  function emptyInner() {
    const inner = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (!inner) return null;
    return inner.classList.contains('cart-drawer--empty') ? inner : null;
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
    const action = event.action || (event.detail && event.detail.action);
    if (action !== 'add') return;

    // Only the broken transition. If the drawer already has contents the
    // theme handles the refresh itself.
    if (!emptyInner()) return;

    try {
      const result = await event.promise;
      if (result && result.detail && result.detail.didError) return;
    } catch (error) {
      return;
    }

    // Re-check after the await: the theme may have fixed it in the meantime.
    if (!emptyInner()) return;

    try {
      const response = await fetch(sectionUrl(), { headers: { Accept: 'text/html' } });
      if (!response.ok) return;

      const markup = await response.text();
      if (!languageMatches(markup)) return;

      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('.cart-drawer__inner');

      if (!fresh || fresh.classList.contains('cart-drawer--empty')) return;

      const target = emptyInner();
      if (target) target.replaceWith(fresh);
    } catch (error) {
      /* leave the drawer as it is rather than breaking it further */
    }
  });
})();